import React, { useRef, useState } from 'react';
import { X, Star, Clock, ArrowRight, CheckCircle, XCircle, Camera, Upload, Scan, AlertCircle, Package2, Heart, MessageSquare, Sparkles } from 'lucide-react';
import axios from 'axios';
import SectionHeader from './SectionHeader';
import { getCompareUrl, getChatRequestUrl } from '../config';
import { useAuth } from '../hooks/useAuth';
import {
  trackComparisonIdentificationComplete,
  trackOutcomeJourneyFailed,
  trackProductComparison,
  trackProductComparisonComplete,
} from '../utils/analytics';
// 2026-06-08: redesigned results view ported from the compare-mock prototype
// (commit 0c35caf). Swipeable carousel, borderless cards, ring-dot pros/cons
// aligned with Product Analysis. Replaces the previous inline results JSX.
import ComparisonResultsRedesign from './ComparisonResultsRedesign';
import { IMAGE_ROLES } from '../utils/imageJourney';

const ProductComparisonView = ({ onBack, availableProducts = [], preselectedProducts = [], userId, saveProduct, onContinueToChat, isAnonymous = false, onShowSignIn, onOpenCatalog, onGatedAction }) => {
  const { getAuthSession } = useAuth();
  const uploadInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const compareImageMapRef = useRef({});
  const [selectedProducts, setSelectedProducts] = useState(preselectedProducts);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [uploadedProducts, setUploadedProducts] = useState([]);
  const [isAnalyzingUpload, setIsAnalyzingUpload] = useState(false);
  // 2026-06-09 (F-14 #2): per-image progress.
  // { total, done, failed } so the upload card can render "Identifying 2 of 3"
  // and stream a checkmark per image as it resolves — instead of one big
  // monolithic "Analysing your photo…" stall for 15-25s.
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [userQuestion, setUserQuestion] = useState(''); // Optional specific question
  const [comparisonProgressMessage, setComparisonProgressMessage] = useState('');
  const [productImageMap, setProductImageMap] = useState({}); // Map product IDs to image URLs

  // Image processing utilities (exact same as ChatComponent)
  const validateImageFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/webp'];
    const maxSize = 50 * 1024 * 1024; // 50MB original file limit

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please upload a valid image file (JPEG, PNG, HEIC, or WebP)');
    }

    if (file.size > maxSize) {
      throw new Error('Image file is too large. Please choose an image under 50MB.');
    }

    return true;
  };

  const resizeAndOptimizeImage = async (file) => {
    // Check if it's a HEIC file and convert to JPEG first
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      console.log('HEIC file detected, converting to JPEG...');
      try {
        const heic2any = (await import('heic2any')).default;
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9
        });
        // Convert blob to file
        file = new File([convertedBlob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
        console.log('✅ HEIC converted to JPEG');
      } catch (error) {
        console.error('HEIC conversion failed:', error);
        throw new Error('Failed to convert HEIC image. Please try a different format.');
      }
    }
    
    return new Promise((resolve, reject) => {
      // OPTIMIZATION: Skip compression for small files to avoid unnecessary processing
      if (file.size < 500000) { // Less than 500KB
        console.log('Small file detected, skipping compression for better performance');
        resolve(file);
        return;
      }
      
      // Use Web Workers for image processing to avoid blocking UI
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        console.log('Original dimensions:', img.width, 'x', img.height);

        // 2026-06-09 (Sofia F-14 #3): smaller max edge for upload + faster
        // vision-API processing. Vision models don't gain meaningful accuracy
        // above ~896px for label OCR. Reducing 1200→896 trims base64 size by
        // ~45% which shaves 1-2s off each upload's transfer + vision call.
        const maxWidth = 896;
        const maxHeight = 896;
        
        let { width, height } = img;
        
        // Resize if needed
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        console.log('Resized dimensions:', width, 'x', height);

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // OPTIMIZATION: Lower quality for better performance, still good for analysis
        canvas.toBlob((blob) => {
          if (blob) {
            console.log('Compression complete. Original:', file.size, 'Compressed:', blob.size);
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        }, 'image/jpeg', 0.8); // Reduced from 0.95 to 0.8 for better performance
      };

      img.onerror = () => {
        console.log('Image load failed, likely HEIC or unsupported format - proceeding with original file');
        resolve(file);
      };
      
      // Create object URL to load the image
      img.src = URL.createObjectURL(file);
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 2026-06-09 (F-14 #1): per-file processor. Extracted from the sequential
  // for-loop so we can Promise.all the work. Each call does its own
  // validate → resize → base64 → POST → parse → Supabase upload. Returns
  // the analyzedProduct (success) or null (handled error, already toasted).
  const processOneFile = async (file, i, totalFiles) => {
      try {
        // Validate and convert image format
        validateImageFile(file);
        const optimizedFile = await resizeAndOptimizeImage(file);
        const base64Data = await fileToBase64(optimizedFile);

              // Get fresh auth token
              const { tokens } = await getAuthSession();
              const accessToken = tokens?.accessToken;
              
              if (!accessToken) {
                throw new Error('Authentication required. Please refresh the page and sign in again.');
              }
              
              // Send to backend for analysis
        console.log(`📤 Uploading product image ${i + 1}/${totalFiles} for analysis...`);
              const response = await axios.post(getChatRequestUrl(), {
                userId: userId,
                message: 'Analyze this product',
                images_data: [base64Data],
                analysis_type: 'product',
                image_role: IMAGE_ROLES.PRODUCT_REFERENCE,
                user_prompt: 'Identify this product'
              }, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
          },
          timeout: 60000  // 60 second timeout for product analysis
              });
              
              if (response.status === 401) {
                throw new Error('Session expired. Please refresh the page and sign in again.');
              }

              console.log(`✅ Upload analysis ${i + 1}/${totalFiles} response:`, response.data);
        console.log(`📦 Response keys:`, Object.keys(response.data || {}));

              // Parse product data from response
              let productData = null;
        if (response.data?.product_data) {
                // Handle both single product object and array
                productData = Array.isArray(response.data.product_data) 
                  ? response.data.product_data[0] 
                  : response.data.product_data;
          console.log(`✅ Found product_data:`, productData);
          console.log(`📦 product_data keys:`, Object.keys(productData || {}));
          console.log(`📦 product_name:`, productData?.product_name, `name:`, productData?.name);
          console.log(`📦 product_brand:`, productData?.productBrand, `brand:`, productData?.brand);
        } else if (response.data?.productAnalysis?.analysis) {
                productData = response.data.productAnalysis.analysis;
          console.log(`✅ Found productAnalysis.analysis:`, productData);
        } else if (response.data?.reply && typeof response.data.reply === 'object') {
                productData = response.data.reply;
          console.log(`✅ Found reply object:`, productData);
        } else {
          console.error(`❌ No product data found in response. Full response:`, response.data);
          throw new Error('Backend did not return product data. Response: ' + JSON.stringify(response.data));
              }

              if (productData) {
          // ✅ FIXED: Check all possible field names from backend
          const productName = productData.product_name || productData.productName || productData.name || 'Uploaded Product';
          const productBrand = productData.productBrand || productData.brand || productData.product_brand || 'Unknown Brand';
          const productCategory = productData.category || productData.product_type || 'Skincare';
          
          // Check if backend returned an error
          if (productName === 'Error' || productBrand === 'Error' || productName === 'Unable to read clearly' || productBrand === 'Unable to read clearly') {
            const errorMsg = productData.error || productData.analysis || 'Failed to analyze product image';
            console.error(`❌ Backend analysis error:`, errorMsg);
            throw new Error(errorMsg);
          }
          
          console.log(`📝 Extracted: name="${productName}", brand="${productBrand}", category="${productCategory}"`);
          
          // Upload image to Supabase Storage to get a permanent public URL.
          //
          // Bug E.2 (2026-05-29): this used to upload to the `images` bucket
          // (private, public: false) and then call getPublicUrl — which
          // returns a non-working URL on a private bucket. <img onError>
          // fired and the selector card showed the Camera placeholder
          // instead of the user's photo. Sofia hit this on the Compare flow.
          //
          // Fix: upload to `product-images` (public bucket), matching the
          // pattern ProductAnalysisView already uses for Take-a-Picture
          // uploads. No URL refresh needed — the public bucket URL is stable.
          const { userId: uploadUserId } = await getAuthSession();
          const { supabase } = await import('../supabase');

          const filePath = `uploaded_images/${uploadUserId}/${Date.now()}_${i}_${file.name}`;
          const { error: uploadError } = await supabase.storage
            .from('product-images')
            .upload(filePath, optimizedFile, {
              cacheControl: '3600',
              upsert: false,
              contentType: optimizedFile.type || 'image/jpeg'
            });

          let analyzedProduct;
          if (uploadError) {
            console.error('⚠️ Failed to upload to Supabase product-images, using temporary blob URL:', uploadError);
            // Fallback to blob URL if upload fails
            const previewUrl = URL.createObjectURL(optimizedFile);
            const productId = `uploaded-${Date.now()}-${Math.random()}`;
            analyzedProduct = {
              id: productId,
              name: productName,
              brand: productBrand,
              category: productCategory,
              rating: 0,
              price: productData.price || '',
              image: previewUrl, // Temporary blob URL as fallback
              usage: productData.best_for || 'As directed',
              notes: 'Analyzed from photo',
              ai_analysis: JSON.stringify(productData)
            };
            setProductImageMap(prev => ({ ...prev, [productId]: previewUrl }));
          } else {
            // product-images is a public bucket so the URL works without signing.
            const { data } = supabase.storage
              .from('product-images')
              .getPublicUrl(filePath);

            const permanentUrl = data.publicUrl;
            console.log(`✅ Uploaded to Supabase product-images, permanent URL: ${permanentUrl}`);

            const productId = `uploaded-${Date.now()}-${Math.random()}`;
            analyzedProduct = {
              id: productId,
              name: productName,
              brand: productBrand,
              category: productCategory,
              rating: 0,
              price: productData.price || '',
              image: permanentUrl, // Permanent public URL (never expires)
              usage: productData.best_for || 'As directed',
              notes: 'Analyzed from photo',
              ai_analysis: JSON.stringify(productData)
            };

            // Store image URL mapping for later use in comparison results
            setProductImageMap(prev => ({
              ...prev,
              [productId]: permanentUrl
            }));

            console.log(`✅ Created analyzedProduct with permanent URL:`, analyzedProduct);
          }
          // Stream the result into uploadedProducts AS SOON as it resolves — so
          // image 1 appears in the list while images 2/3 are still identifying.
          setUploadedProducts(prev => [...prev, analyzedProduct]);
          setUploadProgress(prev => prev ? { ...prev, done: prev.done + 1 } : prev);
          return analyzedProduct;
        } else {
          throw new Error('Could not analyze product from photo');
        }
      } catch (error) {
        console.error(`❌ Error analyzing uploaded product ${i + 1}:`, error);
        console.error(`❌ Error details:`, {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          code: error.code
        });

        // Friendly user-facing message.
        let userMessage = "We couldn't read this product clearly. Try a sharper photo with the label facing the camera.";
        if (error.response?.status === 401) {
          userMessage = 'Session expired. Please refresh and try again.';
        } else if (error.response?.status === 413) {
          userMessage = 'That image is too large — please try a smaller photo.';
        } else if (error.response?.status === 504 || error.code === 'ECONNABORTED') {
          userMessage = 'Analysis took too long. Please try again with a clearer photo.';
        } else if (error.response?.data?.product_data?.error) {
          userMessage = error.response.data.product_data.error;
        }

        setError(`Product ${i + 1}: ${userMessage}`);
        setTimeout(() => setError(null), 6000);
        setUploadProgress(prev => prev ? { ...prev, failed: prev.failed + 1 } : prev);
        return null;
      }
  };

  // 2026-06-09 (F-14 #1 + #2): top-level handler — fans out per-file work in
  // PARALLEL via Promise.all. Wall-clock for 3 files drops from ~3× one call
  // to ~1× (slowest single file). Per-image progress is tracked via
  // uploadProgress state and rendered in the upload card.
  const handleImageUpload = async (event) => {
    if (onGatedAction?.()) {
      event.target.value = '';
      return;
    }
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log('No files selected from file input');
      return;
    }

    console.log(`📸 Processing ${files.length} file(s) in parallel from ${event.target.capture ? 'camera' : 'upload'}`);
    setIsAnalyzingUpload(true);
    setError(null);
    setUploadProgress({ total: files.length, done: 0, failed: 0 });

    const total = files.length;
    const results = await Promise.all(
      Array.from(files).map((file, i) => processOneFile(file, i, total))
    );
    const succeeded = results.filter(Boolean);
    trackComparisonIdentificationComplete({
      total,
      succeeded: succeeded.length,
      failed: total - succeeded.length,
    });

    if (succeeded.length === 0) {
      trackOutcomeJourneyFailed('product_comparison', 'identification_failed');
      setError('Failed to analyze product photos. Please try uploading clearer photos.');
      setTimeout(() => setError(null), 5000);
    }

    setIsAnalyzingUpload(false);
    setUploadProgress(null);
    // Reset file input so user can re-upload the same file if needed
    event.target.value = '';
  };

  const triggerUploadInput = () => {
    if (onGatedAction?.()) return;
    uploadInputRef.current?.click();
  };

  const triggerCameraInput = () => {
    if (onGatedAction?.()) return;
    cameraInputRef.current?.click();
  };

  const handleSelectProduct = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  // 2026-06-09 (Sofia): catalog-selected products store their image in
  // catalog_image_url / image_url, NOT `image`. The old code only checked
  // `product.image`, so comparing catalog products (no uploaded photo) lost
  // the image and the result showed the 🧴 emoji. Resolve all variants.
  const resolveProductImage = (product) =>
    product.image || product.catalog_image_url || product.image_url || product.image_base64 || null;

  const buildProductsPayload = (imageMapRef) =>
    selectedProducts.map((product) => {
      const resolvedImage = resolveProductImage(product);
      if (product.id && resolvedImage) {
        setProductImageMap((prev) => ({ ...prev, [product.id]: resolvedImage }));
        if (imageMapRef) imageMapRef.current[product.id] = resolvedImage;
      }
      const productData = {
        id: product.id,
        // Preserve a known catalogue ID when this saved product has one. Saved
        // list IDs and temporary uploaded IDs still use the existing name/brand
        // resolver; they must never be mistaken for catalogue product IDs.
        ...((product.catalog_product_id || product.catalog_id)
          ? { catalog_product_id: product.catalog_product_id || product.catalog_id }
          : {}),
        name: product.name,
        brand: product.brand,
        category: product.category,
        price: product.price,
        ai_analysis: product.ai_analysis
      };
      if (
        resolvedImage &&
        typeof resolvedImage === 'string' &&
        !resolvedImage.startsWith('data:image') &&
        (resolvedImage.startsWith('http://') || resolvedImage.startsWith('https://'))
      ) {
        productData.image = resolvedImage;
      }
      return productData;
    });

  const applyComparisonResult = (result, imageMapRef) => {
    const map = imageMapRef?.current || productImageMap;
    const productsWithImages = result.products.map((product) => {
      const productId = product.id || product.product_id;
      // Resolve from the result product's own image variants first, then the
      // map (covers both catalog-selected and uploaded products).
      const resolved = resolveProductImage(product) || (productId && map[productId]) || null;
      if (resolved && resolved !== product.image) {
        return { ...product, image: resolved };
      }
      return product;
    });
    setComparisonResult({
      overall: result.overall,
      details: result.details,
      products: productsWithImages
    });
    const winner = result.overall?.winner;
    if (winner && result.products?.length) {
      const winnerProduct = result.products.find((p) => (p.id || p.product_id) === winner);
      if (winnerProduct) trackProductComparisonComplete(`${winnerProduct.brand} ${winnerProduct.name}`);
    }
  };

  const handleCompare = async () => {
    if (onGatedAction?.()) return;
    if (selectedProducts.length < 2) {
      setError('Please select at least 2 products to compare');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsComparing(true);
    setError(null);
    setComparisonProgressMessage('Comparing products...');
    trackProductComparison(selectedProducts.length);

    try {
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      if (!accessToken) {
        throw new Error('Please sign in again to compare products.');
      }

      compareImageMapRef.current = {};
      const body = {
        userId,
        compareProducts: true,
        products: buildProductsPayload(compareImageMapRef),
        userQuestion: userQuestion.trim() || undefined
      };

      const res = await fetch(getCompareUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(200000)  // 200s: backend can do catalog+vision for 4 products + 120s GPT
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || `Compare failed (${res.status})`);
      }
      if (!data.success || !data.result) {
        throw new Error(data.message || 'Unexpected response from server.');
      }
      const result = data.result;
      if (!result || !Array.isArray(result.products)) {
        throw new Error('Invalid comparison result. Please try again.');
      }
      applyComparisonResult(result, compareImageMapRef);
    } catch (err) {
      const msg = err.name === 'TimeoutError' ? 'Comparison timed out. Please try again.' : (err.message || 'Something went wrong.');
      setError(msg);
    } finally {
      setIsComparing(false);
      setComparisonProgressMessage('');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (!comparisonResult) {
    return (
      // 2026-06-09 (Sofia): self-contained padding now that the MenuSheet p-6
      // wrapper is gone. Selection screen gets px-4 pt-4; the RESULT screen
      // (ComparisonResultsRedesign, returned below) stays edge-to-edge.
      <div className="flex flex-col h-full overflow-hidden px-4 pt-4">
        <SectionHeader title="Compare Products" onBack={onBack} className="mb-3 flex-shrink-0" />
        <p className="text-[13px] text-[#2A324B]/55 mb-3">
          {selectedProducts.length === 0
            ? 'Select 2-4 products to compare'
            : `${selectedProducts.length} selected • Select ${selectedProducts.length < 2 ? '1+ more' : 'up to ' + (4 - selectedProducts.length) + ' more'}`}
        </p>

        {/* ✅ FIXED: Error Banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 animate-in fade-in duration-200 flex-shrink-0">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
              {!isComparing && (
                <button
                  onClick={handleCompare}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline"
                >
                  Try again
                </button>
              )}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 pb-4">
            {/* Hidden file inputs for product photo upload */}
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
              className="hidden"
            />
            
            {/* Upload Product Card */}
            <div className="bg-white rounded-2xl shadow-sm p-4 border border-[#F3D5B5]/40">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FDF6EE] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Scan className="h-6 w-6 text-[#C8956A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-[#2A324B] mb-0.5">Compare a Product Photo</h3>
                  <p className="text-xs text-[#2A324B]/50">
                    Upload photos (select multiple for 2-4 at once) or take a photo with your camera
                  </p>
                </div>
              </div>

              {/* Hotfix E.1 (2026-05-29): explicit progress banner during
                  upload analysis. Sofia flagged that "Analyzing..." on just
                  the disabled button left her not knowing what was happening
                  or whether she could do anything else. This banner makes the
                  state legible and reassures the user that the rest of the
                  page is still interactive. */}
              {isAnalyzingUpload && (
                <div className="mt-3 p-3 rounded-2xl bg-[#FDF6EE] border border-[#F3D5B5]/60 flex items-start gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#C8956A] border-t-transparent flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    {/* 2026-06-09 (F-14 #2): show per-image progress + counter
                        when multiple files are uploading in parallel. Single-
                        file case keeps the old copy. */}
                    {uploadProgress && uploadProgress.total > 1 ? (
                      <>
                        <p className="text-xs font-medium text-[#2A324B]">
                          Identifying {Math.min(uploadProgress.done + 1, uploadProgress.total)} of {uploadProgress.total}
                          <span className="ml-2 text-[#2A324B]/55">
                            {'✓'.repeat(uploadProgress.done)}
                            {uploadProgress.failed > 0 && <span className="text-[#C0392B] ml-1">{'✕'.repeat(uploadProgress.failed)}</span>}
                          </span>
                        </p>
                        <p className="text-[13px] text-[#2A324B]/60 mt-0.5 leading-snug">
                          Keep this screen open. You can review products below as they’re recognised.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-[#2A324B]">
                          {comparisonProgressMessage || 'Analysing your photo…'}
                        </p>
                        <p className="text-[13px] text-[#2A324B]/60 mt-0.5 leading-snug">
                          This usually takes 5–10 seconds. You can keep selecting products from your lists below while we work.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <button
                  onClick={triggerUploadInput}
                  disabled={isAnalyzingUpload}
                  className="flex-1 bg-[#2A324B] text-[#F3D5B5] h-10 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#2A324B]/90 transition-colors"
                >
                  {isAnalyzingUpload ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#2A324B] border-t-transparent"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-3 w-3" />
                      Upload Photo
                    </>
                  )}
                </button>
                <button
                  onClick={triggerCameraInput}
                  disabled={isAnalyzingUpload}
                  className="flex-1 border border-[#E0E0E0] text-[#2A324B] h-10 rounded-2xl font-semibold text-sm hover:bg-[#F5F5F5] flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                  <Camera className="h-3 w-3" />
                  Take Photo
                </button>
              </div>
            </div>

            {/* Selected Products */}
            {selectedProducts.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-[#2A324B] font-medium">Selected for Comparison</div>
                  <button
                    onClick={() => setSelectedProducts([])}
                    className="text-xs text-[#2A324B]/70 hover:text-[#2A324B]"
                  >
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedProducts.map((product) => (
                    <div 
                      key={product.id}
                      className="flex items-center gap-3 p-2 bg-[#F3D5B5]/10 rounded-lg"
                    >
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3D5B5]/20 flex-shrink-0 flex items-center justify-center">
                        {(product.catalog_image_url || product.image_url || product.image) ? (
                          <img
                            src={product.catalog_image_url || product.image_url || product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent && !parent.querySelector('.fallback-icon')) {
                                const icon = document.createElement('div');
                                icon.className = 'fallback-icon';
                                icon.innerHTML = '<svg class="h-8 w-8 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
                                parent.appendChild(icon);
                              }
                            }}
                          />
                        ) : (
                          <Camera className="h-8 w-8 text-[#2A324B]/40" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-[#2A324B] truncate">{product.name}</h4>
                        <p className="text-xs text-[#2A324B]/70">{product.brand}</p>
                      </div>
                      <button
                        onClick={() => handleSelectProduct(product)}
                        className="h-6 w-6 flex items-center justify-center hover:bg-white rounded flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  {selectedProducts.length >= 2 && (
                    <div className="space-y-3 mt-4">
                      {/* Required Question Field */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-3.5 w-3.5 text-[#2A324B]/70" />
                          <label className="text-xs font-medium text-[#2A324B]/70">
                            What would you like to compare? <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <textarea
                          value={userQuestion}
                          onChange={(e) => setUserQuestion(e.target.value)}
                          placeholder="e.g., Which one is better for sensitive skin? Which has less fragrance?"
                          className="w-full min-h-[60px] text-sm p-2 border border-[#341C1C]/20 rounded-md resize-none focus:outline-none focus:border-[#F3D5B5] focus:ring-1 focus:ring-[#F3D5B5]"
                          required
                        />
                      </div>
                      
                    <button
                      onClick={handleCompare}
                      disabled={isComparing || !userQuestion.trim()}
                        className="w-full bg-[#2A324B] text-[#F3D5B5] py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#2A324B]/90 transition-colors"
                    >
                      {isComparing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#2A324B] border-t-transparent"></div>
                          {comparisonProgressMessage || 'Comparing...'}
                        </>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4" />
                          Compare {selectedProducts.length} Products
                        </>
                      )}
                    </button>

                    {/* 2026-06-09 (Sofia): reassure the user it takes ~15-20s
                        and to stay on the page — same pattern as the upload
                        analysing banner. Only while a comparison is running. */}
                    {isComparing && (
                      <div className="mt-3 p-3 rounded-2xl bg-[#FDF6EE] border border-[#F3D5B5]/60 flex items-start gap-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#C8956A] border-t-transparent flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#2A324B]">
                            Comparing your products…
                          </p>
                          <p className="text-[13px] text-[#2A324B]/60 mt-0.5 leading-snug">
                            This usually takes 15–20 seconds. Please stay on this page while Son:mi works.
                          </p>
                        </div>
                      </div>
                    )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Uploaded Products */}
            {uploadedProducts.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-[#2A324B] mb-3 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Uploaded Products
                </h3>
                <div className="space-y-2">
                  {uploadedProducts
                    .filter(p => !selectedProducts.find(sp => sp.id === p.id))
                    .map((product) => (
                      <div 
                        key={product.id} 
                        className="border border-[#F3D5B5]/50 bg-white cursor-pointer hover:border-[#F3D5B5] transition-all rounded-lg p-3"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className="flex gap-3">
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3D5B5]/20 flex-shrink-0 flex items-center justify-center">
                            {(product.catalog_image_url || product.image_url || product.image) ? (
                              <img
                                src={product.catalog_image_url || product.image_url || product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  if (parent && !parent.querySelector('.fallback-icon')) {
                                    const icon = document.createElement('div');
                                    icon.className = 'fallback-icon';
                                    icon.innerHTML = '<svg class="h-8 w-8 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
                                    parent.appendChild(icon);
                                  }
                                }}
                              />
                            ) : (
                              <Camera className="h-8 w-8 text-[#2A324B]/40" />
                            )}
                          </div>
                          
                          {/* 2026-06-09 (Sofia feedback): dropped the "From
                              photo" pill (redundant — it's already in a
                              "Uploaded Products" section header) and the price
                              column (we don't surface pricing anywhere). The
                              row reads cleaner with just name + brand. */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-[#2A324B] text-sm leading-tight">{product.name}</h3>
                            <p className="text-xs text-[#2A324B]/70 mt-0.5">{product.brand}</p>
                          </div>
                          
                          <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              selectedProducts.find(p => p.id === product.id)
                                ? 'border-[#F3D5B5] bg-[#F3D5B5]'
                                : 'border-[#341C1C]/20 bg-white'
                            }`}>
                              {selectedProducts.find(p => p.id === product.id) && (
                                <CheckCircle className="h-4 w-4 text-[#2A324B]" />
                              )}
                            </div>
                          </div>
                        </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Products */}
            <div>
              <h3 className="text-sm font-medium text-[#2A324B] mb-3">
                {uploadedProducts.length > 0 ? 'Your Product Lists' : selectedProducts.length === 0 ? 'Select products to compare' : 'Add more products'}
              </h3>
              {availableProducts.length === 0 ? (
                <div className="border border-[#341C1C]/10 rounded-lg bg-white p-6 text-center">
                  <div className="w-12 h-12 bg-[#F3D5B5]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package2 className="h-6 w-6 text-[#2A324B]/60" />
                  </div>
                  <h4 className="text-sm font-medium text-[#2A324B] mb-1">No saved products yet</h4>
                  <p className="text-sm text-[#2A324B]/70 mb-3">
                    {isAnonymous
                      ? 'Upload product photos above, or create a free account to save products for future comparisons.'
                      : 'Upload product photos above or choose products you have already saved.'}
                  </p>
                  <button
                    onClick={() => isAnonymous ? onShowSignIn?.() : onOpenCatalog?.()}
                    className="text-sm text-[#B06F4F] hover:text-[#8F563D] font-semibold"
                  >
                    {isAnonymous ? 'Create account to save products →' : 'Open my saved products →'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableProducts
                    .filter(p => !selectedProducts.find(sp => sp.id === p.id))
                    .map((product) => (
                    <div 
                      key={product.id} 
                      className={`border border-[#341C1C]/10 bg-white cursor-pointer transition-all rounded-lg p-3 ${
                        selectedProducts.length >= 4 ? 'opacity-50' : 'hover:border-[#F3D5B5]'
                      }`}
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="flex gap-3">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F3D5B5]/20 flex-shrink-0">
                          <img
                            src={product.catalog_image_url || product.image_url || product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="font-medium text-[#2A324B] text-sm leading-tight">{product.name}</h3>
                              <p className="text-xs text-[#2A324B]/70 mt-0.5">{product.brand}</p>
                              {renderStars(product.rating)}
                            </div>
                            {/* 2026-06-09: price hidden (we don't surface
                                pricing anywhere). Suggestion score chip kept. */}
                            {product.suggestion && (
                              <div className="text-right flex-shrink-0">
                                <span className="inline-block text-xs px-2 py-0.5 border rounded">
                                  {product.suggestion.score}%
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* 2026-06-09: removed the mystery clock+usage pill
                              ("Clock ⏱ As directed") — Sofia flagged users
                              didn't understand what the clock meant. Kept the
                              category chip only. */}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs bg-[#F3D5B5]/30 text-[#2A324B] px-2 py-0.5 rounded">
                              {product.category}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedProducts.find(p => p.id === product.id)
                              ? 'border-[#F3D5B5] bg-[#F3D5B5]'
                              : 'border-[#341C1C]/20 bg-white'
                          }`}>
                            {selectedProducts.find(p => p.id === product.id) && (
                              <CheckCircle className="h-4 w-4 text-[#2A324B]" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Comparison Results View — delegated to ComparisonResultsRedesign
  // (the borderless / swipe-carousel redesign ported from the compare-mock
  // prototype, commit 0c35caf). The component encapsulates the entire view
  // (header, dots, carousel, bottom actions).
  //
  // 2026-06-11 (Sofia): "Ask a question" was a dead button (onAsk=onBack →
  // back to catalog, not chat). Now it routes to chat via onContinueToChat,
  // prefilling a question that references the compared products so the
  // follow-up relates to the analysis the user just saw.
  const handleAskAboutComparison = () => {
    const list = comparisonResult.products || selectedProducts || [];
    const names = list.map((p) => p.name).filter(Boolean);
    if (onContinueToChat) {
      onContinueToChat({
        comparison: {
          productNames: names,
          winnerId: comparisonResult?.overall?.winner || null,
        },
      });
    } else {
      onBack();
    }
  };

  return (
    <ComparisonResultsRedesign
      comparisonResult={comparisonResult}
      products={comparisonResult.products || selectedProducts}
      onBack={onBack}
      onCompareAgain={() => {
        setComparisonResult(null);
        setSelectedProducts([]);
      }}
      saveProduct={saveProduct}
      onAsk={handleAskAboutComparison}
    />
  );
};

export default ProductComparisonView;
